import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Package, Search, Eye, DollarSign, Clock, Truck, CheckCircle, XCircle, ArrowLeft, Trash2, AlertTriangle, Printer, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getProxiedImageUrl } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {order_id, customer} for delete confirmation
  const [selectedForPrint, setSelectedForPrint] = useState([]); // Array of order IDs selected for printing
  const [printMode, setPrintMode] = useState(false); // Toggle print view
  const [editMode, setEditMode] = useState(false); // Toggle edit mode
  const [editedOrder, setEditedOrder] = useState(null); // Edited order data

  useEffect(() => {
    fetchOrders();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      
      const response = await fetch(`${API_URL}/api/orders?${params}`);
      const data = await response.json();
      setOrders(data.orders || []);
      setLoading(false);
    } catch (error) {
      toast.error('Errore caricamento ordini');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/stats/summary`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PATCH'
      });

      if (response.ok) {
        toast.success(`Stato aggiornato: ${newStatus}`);
        fetchOrders();
        fetchStats();
        if (selectedOrder && selectedOrder.order_id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus });
        }
      } else {
        toast.error('Errore aggiornamento stato');
      }
    } catch (error) {
      toast.error('Errore connessione');
    }
  };

  const handleResendEmail = async (orderId) => {
    if (!window.confirm(`Inviare nuovamente l'email di conferma per l'ordine ${orderId}?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/resend-order-email/${orderId}`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Email inviata a ${data.email}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore invio email');
      }
    } catch (error) {
      toast.error('Errore connessione');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Ordine ${orderId} eliminato!`);
        
        // Refresh orders and stats
        fetchOrders();
        fetchStats();
        setDeleteConfirm(null);
        
        // Close modal if the deleted order was selected
        if (selectedOrder && selectedOrder.order_id === orderId) {
          setSelectedOrder(null);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore eliminazione ordine');
      }
    } catch (error) {
      toast.error('Errore connessione');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30', label: 'In Attesa', icon: Clock },
      processing: { color: 'bg-blue-500/20 text-blue-500 border-blue-500/30', label: 'In Elaborazione', icon: Package },
      shipped: { color: 'bg-purple-500/20 text-purple-500 border-purple-500/30', label: 'Spedito', icon: Truck },
      delivered: { color: 'bg-green-500/20 text-green-500 border-green-500/30', label: 'Consegnato', icon: CheckCircle },
      cancelled: { color: 'bg-red-500/20 text-red-500 border-red-500/30', label: 'Annullato', icon: XCircle }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${badge.color}`}>
        <Icon size={14} />
        {badge.label}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus, paymentMethod) => {
    const isCompleted = paymentStatus === 'completed';
    return (
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
          isCompleted ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
        }`}>
          {isCompleted ? '✓ Pagato' : '⏳ In Attesa'}
        </span>
        {paymentMethod && (
          <span className="text-xs text-white/60">
            {paymentMethod === 'stripe' && '💳 Stripe'}
            {paymentMethod === 'paypal' && 'PayPal'}
            {paymentMethod === 'cod' && '📦 Contrassegno'}
            {paymentMethod.includes('bank_transfer') && '🏦 Bonifico'}
          </span>
        )}
      </div>
    );
  };

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      order.order_id.toLowerCase().includes(search) ||
      order.customer.email.toLowerCase().includes(search) ||
      order.customer.name.toLowerCase().includes(search)
    );
  });

  // Toggle select all orders for printing
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedForPrint(filteredOrders.map(o => o.order_id));
    } else {
      setSelectedForPrint([]);
    }
  };

  // Toggle individual order selection
  const handleSelectOrder = (orderId) => {
    setSelectedForPrint(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Handle print action
  const handlePrint = () => {
    if (selectedForPrint.length === 0) {
      toast.error('Seleziona almeno un ordine da stampare');
      return;
    }
    setPrintMode(true);
    // Trigger browser print after render
    setTimeout(() => {
      window.print();
    }, 500);
  };

  // Close print mode after printing
  useEffect(() => {
    const handleAfterPrint = () => {
      setPrintMode(false);
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enable edit mode
  const handleEnableEdit = () => {
    setEditedOrder(JSON.parse(JSON.stringify(selectedOrder))); // Deep clone
    setEditMode(true);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedOrder(null);
  };

  // Update edited field
  const updateEditedField = (path, value) => {
    setEditedOrder(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      
      // Recalculate total
      const subtotal = parseFloat(updated.subtotal) || 0;
      const shipping = parseFloat(updated.shipping_cost) || 0;
      const cod = parseFloat(updated.cod_fee) || 0;
      updated.total = subtotal + shipping + cod;
      
      return updated;
    });
  };

  // Save edited order
  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/api/orders/${editedOrder.order_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer: editedOrder.customer,
          shipping_address: editedOrder.shipping_address,
          items: editedOrder.items,
          subtotal: editedOrder.subtotal,
          shipping_cost: editedOrder.shipping_cost,
          cod_fee: editedOrder.cod_fee,
          total: editedOrder.total,
          notes: editedOrder.notes,
          tracking_number: editedOrder.tracking_number || '',
          carrier: editedOrder.carrier || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Ordine aggiornato con successo!');
        setSelectedOrder(data.order);
        setEditMode(false);
        setEditedOrder(null);
        fetchOrders();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore aggiornamento ordine');
      }
    } catch (error) {
      toast.error('Errore connessione');
    }
  };

  return (
    <div className="dark-container pt-20 min-h-screen">
      <div className="max-w-[1800px] mx-auto px-6 lg:px-[7.6923%] py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button 
              onClick={() => navigate('/admin/inventory')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4"
            >
              <ArrowLeft size={20} />
              {language === 'it' ? 'Torna a Inventario' : 'Back to Inventory'}
            </button>
            <div className="flex gap-2 mb-4">
              <button 
                onClick={() => navigate('/admin/import')}
                className="btn-secondary text-sm py-2 px-4"
              >
                📥 {language === 'it' ? 'Import' : 'Import'}
              </button>
              <button 
                onClick={() => navigate('/admin/newsletter')}
                className="btn-secondary text-sm py-2 px-4"
              >
                📧 {language === 'it' ? 'Newsletter' : 'Newsletter'}
              </button>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              {language === 'it' ? 'Gestione Ordini' : 'Orders Management'}
            </h1>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-3">
                <Package size={24} className="text-primary" />
                <div>
                  <div className="text-2xl font-bold">{stats.total_orders}</div>
                  <div className="text-sm text-white/60">Totali</div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
              <div className="flex items-center gap-3">
                <Clock size={24} className="text-yellow-500" />
                <div>
                  <div className="text-2xl font-bold text-yellow-500">{stats.pending_orders}</div>
                  <div className="text-sm text-white/60">In Attesa</div>
                </div>
              </div>
            </div>

            <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
              <div className="flex items-center gap-3">
                <Package size={24} className="text-blue-500" />
                <div>
                  <div className="text-2xl font-bold text-blue-500">{stats.processing_orders}</div>
                  <div className="text-sm text-white/60">Elaborazione</div>
                </div>
              </div>
            </div>

            <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
              <div className="flex items-center gap-3">
                <Truck size={24} className="text-purple-500" />
                <div>
                  <div className="text-2xl font-bold text-purple-500">{stats.shipped_orders}</div>
                  <div className="text-sm text-white/60">Spediti</div>
                </div>
              </div>
            </div>

            <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle size={24} className="text-green-500" />
                <div>
                  <div className="text-2xl font-bold text-green-500">{stats.delivered_orders}</div>
                  <div className="text-sm text-white/60">Consegnati</div>
                </div>
              </div>
            </div>

            <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
              <div className="flex items-center gap-3">
                <DollarSign size={24} className="text-primary" />
                <div>
                  <div className="text-2xl font-bold text-primary">€{stats.total_revenue}</div>
                  <div className="text-sm text-white/60">Ricavi</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/5 rounded-lg p-6 border border-white/10 mb-6">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm mb-2">Cerca Ordine</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="ID ordine, email, nome cliente..."
                  className="w-full bg-black/30 border border-white/20 rounded pl-10 pr-4 py-2 focus:outline-none focus:border-primary"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm mb-2">Filtra per Stato</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
              >
                <option value="all">Tutti gli Stati</option>
                <option value="pending">In Attesa</option>
                <option value="processing">In Elaborazione</option>
                <option value="shipped">Spedito</option>
                <option value="delivered">Consegnato</option>
                <option value="cancelled">Annullato</option>
              </select>
            </div>
          </div>
        </div>

        {/* Print Button */}
        {selectedForPrint.length > 0 && (
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Printer size={24} className="text-primary" />
              <span className="font-semibold">
                {selectedForPrint.length} {selectedForPrint.length === 1 ? 'ordine selezionato' : 'ordini selezionati'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedForPrint([])}
                className="btn-secondary text-sm py-2 px-4"
              >
                Annulla Selezione
              </button>
              <button
                onClick={handlePrint}
                className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
              >
                <Printer size={18} />
                Stampa Selezionati
              </button>
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          {/* Mobile scroll hint */}
          <div className="md:hidden bg-blue-500/10 border-b border-blue-500/30 px-4 py-2 text-sm text-blue-400 text-center">
            {language === 'it' ? '← Scorri orizzontalmente per vedere tutte le colonne →' : '← Scroll horizontally to see all columns →'}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-4 py-3 text-center text-sm font-semibold">
                    <input
                      type="checkbox"
                      checked={selectedForPrint.length === filteredOrders.length && filteredOrders.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 accent-primary cursor-pointer"
                      title="Seleziona tutti"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">ID Ordine</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Cliente</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Data</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Articoli</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Totale</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Pagamento</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Stato</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Tracking</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Azioni</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Elimina</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center text-white/60">
                      Caricamento...
                    </td>
                  </tr>
                ) : filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center text-white/60">
                      Nessun ordine trovato
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map(order => (
                    <tr key={order.order_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedForPrint.includes(order.order_id)}
                          onChange={() => handleSelectOrder(order.order_id)}
                          className="w-4 h-4 accent-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-sm font-semibold text-primary">
                          {order.order_id}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{order.customer.name}</div>
                        <div className="text-xs text-white/60">{order.customer.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {new Date(order.created_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                        <div className="text-xs text-white/60">
                          {new Date(order.created_at).toLocaleTimeString('it-IT', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold">
                        {order.items.length}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-primary">
                        €{order.total.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getPaymentBadge(order.payment_status, order.payment_method)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3 text-center text-xs">
                        {order.carrier ? (
                          <div>
                            <span className="text-primary font-semibold">{order.carrier}</span>
                            {order.tracking_number && (
                              <div className="font-mono text-white/60 mt-0.5">{order.tracking_number}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-white/30">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="btn-secondary text-xs py-1 px-3 inline-flex items-center gap-1"
                        >
                          <Eye size={14} />
                          Dettagli
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setDeleteConfirm({ order_id: order.order_id, customer: order.customer.name })}
                          className="p-2 hover:bg-red-500/20 text-red-500 rounded transition-colors"
                          title="Elimina ordine"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 text-sm text-white/60 text-center">
          Mostrando {filteredOrders.length} di {orders.length} ordini
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setSelectedOrder(null);
            setEditMode(false);
            setEditedOrder(null);
          }}
        >
          <div 
            className="bg-[#0a0a0a] border border-white/10 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 md:p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-2">
                    {editMode ? 'Modifica Ordine' : 'Dettagli Ordine'}
                  </h2>
                  <p className="font-mono text-primary text-sm md:text-base">{selectedOrder.order_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  {!editMode && (
                    <button
                      onClick={handleEnableEdit}
                      className="btn-primary text-sm py-2 px-4"
                    >
                      ✏️ Modifica
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedOrder(null);
                      setEditMode(false);
                      setEditedOrder(null);
                    }}
                    className="text-white/60 hover:text-white text-2xl"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {editMode ? (
                /* EDIT MODE */
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
                    {/* Customer Info - Editable */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="font-semibold mb-3">👤 Cliente</h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <label className="text-white/60 text-xs">Nome:</label>
                          <input
                            type="text"
                            value={editedOrder.customer.name}
                            onChange={(e) => updateEditedField('customer.name', e.target.value)}
                            className="w-full bg-black/30 border border-white/20 rounded px-2 py-1 mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-white/60 text-xs">Email:</label>
                          <input
                            type="email"
                            value={editedOrder.customer.email}
                            onChange={(e) => updateEditedField('customer.email', e.target.value)}
                            className="w-full bg-black/30 border border-white/20 rounded px-2 py-1 mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-white/60 text-xs">Telefono:</label>
                          <input
                            type="text"
                            value={editedOrder.customer.phone}
                            onChange={(e) => updateEditedField('customer.phone', e.target.value)}
                            className="w-full bg-black/30 border border-white/20 rounded px-2 py-1 mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Shipping Address - Editable */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="font-semibold mb-3">📍 Indirizzo Spedizione</h3>
                      <div className="space-y-2 text-sm">
                        <input
                          type="text"
                          value={editedOrder.shipping_address.address}
                          onChange={(e) => updateEditedField('shipping_address.address', e.target.value)}
                          className="w-full bg-black/30 border border-white/20 rounded px-2 py-1"
                          placeholder="Indirizzo"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={editedOrder.shipping_address.city}
                            onChange={(e) => updateEditedField('shipping_address.city', e.target.value)}
                            className="w-full bg-black/30 border border-white/20 rounded px-2 py-1"
                            placeholder="Città"
                          />
                          <input
                            type="text"
                            value={editedOrder.shipping_address.postal_code}
                            onChange={(e) => updateEditedField('shipping_address.postal_code', e.target.value)}
                            className="w-full bg-black/30 border border-white/20 rounded px-2 py-1"
                            placeholder="CAP"
                          />
                        </div>
                        <input
                          type="text"
                          value={editedOrder.shipping_address.province}
                          onChange={(e) => updateEditedField('shipping_address.province', e.target.value)}
                          className="w-full bg-black/30 border border-white/20 rounded px-2 py-1"
                          placeholder="Provincia"
                        />
                        <input
                          type="text"
                          value={editedOrder.shipping_address.country}
                          onChange={(e) => updateEditedField('shipping_address.country', e.target.value)}
                          className="w-full bg-black/30 border border-white/20 rounded px-2 py-1"
                          placeholder="Paese"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Products - Read-only in this version */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold mb-3">🛍️ Prodotti</h3>
                    <div className="space-y-3">
                      {editedOrder.items.map((item, idx) => (
                        <div key={item.sku || idx} className="flex items-center gap-4 bg-white/5 rounded p-3">
                          {item.image && (
                            <img 
                              src={getProxiedImageUrl(item.image)} 
                              alt={item.name_it}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <div className="font-medium">{item.name_it}</div>
                            <div className="text-sm text-white/60">SKU: {item.sku}</div>
                            <div className="text-sm">Quantità: {item.quantity}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-primary">€{item.subtotal.toFixed(2)}</div>
                            <div className="text-xs text-white/60">€{item.price.toFixed(2)} cad.</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary - Editable */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold mb-3">💰 Importi</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span>Subtotale:</span>
                        <input
                          type="number"
                          step="0.01"
                          value={editedOrder.subtotal}
                          onChange={(e) => updateEditedField('subtotal', parseFloat(e.target.value) || 0)}
                          className="w-32 bg-black/30 border border-white/20 rounded px-2 py-1 text-right"
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Spedizione:</span>
                        <input
                          type="number"
                          step="0.01"
                          value={editedOrder.shipping_cost}
                          onChange={(e) => updateEditedField('shipping_cost', parseFloat(e.target.value) || 0)}
                          className="w-32 bg-black/30 border border-white/20 rounded px-2 py-1 text-right"
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Supplemento Contrassegno:</span>
                        <input
                          type="number"
                          step="0.01"
                          value={editedOrder.cod_fee || 0}
                          onChange={(e) => updateEditedField('cod_fee', parseFloat(e.target.value) || 0)}
                          className="w-32 bg-black/30 border border-white/20 rounded px-2 py-1 text-right"
                        />
                      </div>
                      <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                        <span>Totale:</span>
                        <span className="text-primary">€{editedOrder.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes - Editable */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold mb-2">📝 Note Ordine</h3>
                    <textarea
                      value={editedOrder.notes || ''}
                      onChange={(e) => updateEditedField('notes', e.target.value)}
                      className="w-full bg-black/30 border border-white/20 rounded px-3 py-2 text-sm min-h-[80px]"
                      placeholder="Aggiungi note..."
                    />
                  </div>

                  {/* Tracking & Carrier - Editable */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold mb-3">🚚 Spedizione & Tracking</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-white/60 mb-1">Corriere</label>
                        <select
                          value={editedOrder.carrier || ''}
                          onChange={(e) => updateEditedField('carrier', e.target.value)}
                          className="w-full bg-black/30 border border-white/20 rounded px-3 py-2 text-sm"
                          data-testid="carrier-select"
                        >
                          <option value="">-- Seleziona Corriere --</option>
                          <option value="BRT">BRT (Bartolini)</option>
                          <option value="GLS">GLS</option>
                          <option value="SDA">SDA</option>
                          <option value="Poste Italiane">Poste Italiane</option>
                          <option value="DHL">DHL</option>
                          <option value="FedEx">FedEx</option>
                          <option value="UPS">UPS</option>
                          <option value="TNT">TNT</option>
                          <option value="Altro">Altro</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-white/60 mb-1">Numero Tracking</label>
                        <input
                          type="text"
                          value={editedOrder.tracking_number || ''}
                          onChange={(e) => updateEditedField('tracking_number', e.target.value)}
                          className="w-full bg-black/30 border border-white/20 rounded px-3 py-2 text-sm"
                          placeholder="es: BRT123456789"
                          data-testid="tracking-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Edit Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 btn-secondary py-3"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="flex-1 btn-primary py-3"
                    >
                      💾 Salva Modifiche
                    </button>
                  </div>
                </>
              ) : (
                /* VIEW MODE */
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
                    {/* Customer Info */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="font-semibold mb-3">👤 Cliente</h3>
                      <div className="space-y-2 text-sm">
                        <div><span className="text-white/60">Nome:</span> {selectedOrder.customer.name}</div>
                        <div><span className="text-white/60">Email:</span> {selectedOrder.customer.email}</div>
                        <div><span className="text-white/60">Telefono:</span> {selectedOrder.customer.phone}</div>
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <h3 className="font-semibold mb-3">📍 Indirizzo Spedizione</h3>
                      <div className="space-y-1 text-sm">
                        <div>{selectedOrder.shipping_address.address}</div>
                        <div>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.postal_code}</div>
                        <div>{selectedOrder.shipping_address.province}</div>
                        <div>{selectedOrder.shipping_address.country}</div>
                      </div>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                    <h3 className="font-semibold mb-3">🛍️ Prodotti</h3>
                    <div className="space-y-3">
                      {selectedOrder.items.map((item, idx) => (
                        <div key={item.sku || idx} className="flex items-center gap-4 bg-white/5 rounded p-3">
                          {item.image && (
                            <img 
                              src={getProxiedImageUrl(item.image)} 
                              alt={item.name_it}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <div className="font-medium">{item.name_it}</div>
                            <div className="text-sm text-white/60">SKU: {item.sku}</div>
                            <div className="text-sm">Quantità: {item.quantity}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-primary">€{item.subtotal.toFixed(2)}</div>
                            <div className="text-xs text-white/60">€{item.price.toFixed(2)} cad.</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 mb-6">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Subtotale:</span>
                        <span>€{selectedOrder.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Spedizione:</span>
                        <span>€{selectedOrder.shipping_cost.toFixed(2)}</span>
                      </div>
                      {selectedOrder.cod_fee && (
                        <div className="flex justify-between text-yellow-500">
                          <span>Supplemento Contrassegno:</span>
                          <span>€{selectedOrder.cod_fee.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                        <span>Totale:</span>
                        <span className="text-primary">€{selectedOrder.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Status Management */}
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <h3 className="font-semibold mb-3">📦 Gestione Stato</h3>
                    <div className="flex flex-wrap gap-2">
                      {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
                        <button
                          key={status}
                          onClick={() => updateOrderStatus(selectedOrder.order_id, status)}
                          disabled={selectedOrder.status === status}
                          className={`px-3 md:px-4 py-2 rounded text-xs md:text-sm font-semibold transition-colors ${
                            selectedOrder.status === status
                              ? 'bg-primary text-black cursor-not-allowed'
                              : 'bg-white/10 hover:bg-white/20'
                          }`}
                        >
                          {status === 'pending' && '⏳ In Attesa'}
                          {status === 'processing' && '📦 In Elaborazione'}
                          {status === 'shipped' && '🚚 Spedito'}
                          {status === 'delivered' && '✅ Consegnato'}
                          {status === 'cancelled' && '❌ Annullato'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tracking & Carrier Info */}
                  {(selectedOrder.carrier || selectedOrder.tracking_number) && (
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10 mt-4">
                      <h3 className="font-semibold mb-3">🚚 Spedizione & Tracking</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {selectedOrder.carrier && (
                          <div>
                            <span className="text-white/60">Corriere:</span>
                            <span className="ml-2 font-semibold text-primary">{selectedOrder.carrier}</span>
                          </div>
                        )}
                        {selectedOrder.tracking_number && (
                          <div>
                            <span className="text-white/60">Tracking:</span>
                            <span className="ml-2 font-mono font-semibold">{selectedOrder.tracking_number}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedOrder.notes && (
                    <div className="bg-white/5 rounded-lg p-4 border border-white/10 mt-4">
                      <h3 className="font-semibold mb-2">📝 Note Ordine</h3>
                      <p className="text-sm text-white/80">{selectedOrder.notes}</p>
                    </div>
                  )}

                  {/* Action Buttons in Modal */}
                  <div className="mt-6 pt-4 border-t border-white/10 space-y-3">
                    <button
                      onClick={() => handleResendEmail(selectedOrder.order_id)}
                      className="w-full px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded transition-colors font-semibold flex items-center justify-center gap-2"
                      data-testid="resend-email-btn"
                    >
                      <Mail size={18} />
                      {language === 'it' ? 'Reinvia Email Conferma' : 'Resend Confirmation Email'}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedOrder(null);
                        setDeleteConfirm({ order_id: selectedOrder.order_id, customer: selectedOrder.customer.name });
                      }}
                      className="w-full px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-500 rounded transition-colors font-semibold flex items-center justify-center gap-2"
                    >
                      <Trash2 size={18} />
                      {language === 'it' ? 'Elimina Ordine' : 'Delete Order'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div 
            className="bg-[#0a0a0a] border border-red-500/50 rounded-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={32} className="text-red-500" />
              <h2 className="text-xl font-bold">
                {language === 'it' ? 'Conferma Eliminazione' : 'Confirm Deletion'}
              </h2>
            </div>
            
            <p className="text-white/80 mb-6">
              {language === 'it' 
                ? `Sei sicuro di voler eliminare definitivamente l'ordine ${deleteConfirm.order_id} del cliente "${deleteConfirm.customer}"?` 
                : `Are you sure you want to permanently delete order ${deleteConfirm.order_id} from customer "${deleteConfirm.customer}"?`}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
              >
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </button>
              <button
                onClick={() => handleDeleteOrder(deleteConfirm.order_id)}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors font-semibold"
              >
                {language === 'it' ? 'Elimina' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print View - rendered via portal directly into <body> so the
          `body > *:not(.print-only)` CSS rule correctly hides every other
          element of the page (avoids blank trailing pages). */}
      {printMode && createPortal(
        <div className="print-only">
          {orders
            .filter(order => selectedForPrint.includes(order.order_id))
            .map((order, index) => (
              <div key={order.order_id} className={`print-page ${index > 0 ? 'page-break' : ''}`}>
                <div className="print-order">
                  {/* Header */}
                  <div className="print-header">
                    <h1 className="print-title">ALLMX</h1>
                    <div className="print-order-id">{order.order_id}</div>
                  </div>

                  {/* Customer & Shipping in 2 columns */}
                  <div className="print-section-grid">
                    {/* Customer */}
                    <div className="print-box">
                      <h3 className="print-section-title">👤 Cliente</h3>
                      <div className="print-detail"><strong>Nome:</strong> {order.customer.name}</div>
                      <div className="print-detail"><strong>Email:</strong> {order.customer.email}</div>
                      <div className="print-detail"><strong>Telefono:</strong> {order.customer.phone}</div>
                    </div>

                    {/* Shipping Address */}
                    <div className="print-box">
                      <h3 className="print-section-title">📍 Indirizzo Spedizione</h3>
                      <div className="print-detail">{order.shipping_address.address}</div>
                      <div className="print-detail">{order.shipping_address.city}, {order.shipping_address.postal_code}</div>
                      <div className="print-detail">{order.shipping_address.province}</div>
                      <div className="print-detail">{order.shipping_address.country}</div>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="print-box">
                    <h3 className="print-section-title">🛍️ Prodotti</h3>
                    {order.items.map((item, idx) => (
                      <div key={item.sku || idx} className="print-product">
                        <div className="print-product-info">
                          <div className="print-product-name">{item.name_it}</div>
                          <div className="print-product-meta">
                            <span>SKU: {item.sku}</span>
                            <span>Quantità: {item.quantity}</span>
                          </div>
                        </div>
                        <div className="print-product-price">
                          <div className="print-price-main">€{item.subtotal.toFixed(2)}</div>
                          <div className="print-price-unit">€{item.price.toFixed(2)} cad.</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div className="print-box print-summary">
                    <div className="print-summary-row">
                      <span>Subtotale:</span>
                      <span>€{order.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="print-summary-row">
                      <span>Spedizione:</span>
                      <span>€{order.shipping_cost.toFixed(2)}</span>
                    </div>
                    {order.cod_fee && (
                      <div className="print-summary-row print-cod">
                        <span>Supplemento Contrassegno:</span>
                        <span>€{order.cod_fee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="print-summary-row print-total">
                      <span>Totale:</span>
                      <span>€{order.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>,
        document.body
      )}

      <style jsx>{`
        @media print {
          /* Reset */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          /* Hide every direct child of <body> by default, then show only
             the print container. Using display:none (instead of
             visibility:hidden) prevents fixed/absolute UI (header, promo
             banner, footer, WhatsApp button, modals) from reserving empty
             space and producing blank trailing pages. */
          body > *:not(.print-only) {
            display: none !important;
          }

          .print-only {
            display: block !important;
            position: static !important;
            width: 100%;
            background: white;
            color: black;
          }

          /* Page setup */
          @page {
            size: A4;
            margin: 10mm;
          }

          /* Force a page break BEFORE every order except the first.
             We intentionally do NOT set page-break-after on .print-page,
             since that would emit a trailing blank page after the last one. */
          .page-break {
            page-break-before: always;
            break-before: page;
          }

          /* Print layout - centered and constrained */
          .print-page {
            width: 100%;
            max-width: 190mm;
            margin: 0 auto;
            padding: 0;
            background: white;
            color: black;
            overflow: hidden;
          }

          .print-order {
            font-family: Arial, sans-serif;
            width: 100%;
          }

          .print-header {
            text-align: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 3px solid #FF6600;
          }

          .print-title {
            font-size: 28px;
            font-weight: bold;
            color: #FF6600;
            margin: 0 0 8px 0;
          }

          .print-order-id {
            font-size: 13px;
            color: #666;
            font-family: monospace;
          }

          .print-section-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 12px;
          }

          .print-box {
            border: 1px solid #ddd;
            padding: 10px;
            border-radius: 4px;
            margin-bottom: 12px;
          }

          .print-section-title {
            font-size: 13px;
            font-weight: bold;
            margin: 0 0 8px 0;
            color: #333;
          }

          .print-detail {
            font-size: 11px;
            margin: 4px 0;
            color: #555;
            line-height: 1.4;
          }

          .print-product {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 8px 0;
            border-bottom: 1px solid #eee;
          }

          .print-product:last-child {
            border-bottom: none;
          }

          .print-product-info {
            flex: 1;
            padding-right: 10px;
          }

          .print-product-name {
            font-size: 12px;
            font-weight: 600;
            margin-bottom: 3px;
            color: #222;
            line-height: 1.3;
          }

          .print-product-meta {
            font-size: 10px;
            color: #666;
            display: flex;
            gap: 12px;
          }

          .print-product-price {
            text-align: right;
            flex-shrink: 0;
          }

          .print-price-main {
            font-size: 13px;
            font-weight: bold;
            color: #FF6600;
          }

          .print-price-unit {
            font-size: 10px;
            color: #888;
          }

          .print-summary {
            background: #f9f9f9;
            margin-top: 15px;
          }

          .print-summary-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            font-size: 12px;
          }

          .print-cod {
            color: #FF6600;
            font-weight: 600;
          }

          .print-total {
            border-top: 2px solid #FF6600;
            margin-top: 6px;
            padding-top: 10px;
            font-size: 15px;
            font-weight: bold;
          }

          .print-total span:last-child {
            color: #FF6600;
            font-size: 16px;
          }
        }

        @media screen {
          .print-only {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminOrders;
